import { forwardRef, useMemo, useState } from 'react'
import {Triangle} from 'lucide-react'
import SolverCard from '@/components/SolverCard'
import CopyButton from '@/components/CopyButton'
import EmailSolutionButton from '@/components/EmailSolutionButton'

interface Props { highlighted: boolean }

const TrapezoidCalculator = forwardRef<HTMLElement, Props>(({ highlighted }, ref) => {
  const [topBase, setTopBase] = useState(8)
  const [bottomBase, setBottomBase] = useState(14)
  const [height, setHeight] = useState(6)

  const area = ((topBase + bottomBase) / 2) * height
  const mid = (topBase + bottomBase) / 2

  const md = useMemo(() => `# Trapezoid Area\n\n- Top base a = ${topBase}\n- Bottom base b = ${bottomBase}\n- Height h = ${height}\n- Formula: A = ((a + b) / 2) · h\n- Computation: A = ((${topBase} + ${bottomBase}) / 2) · ${height} = ${mid} · ${height}\n- Result: **A = ${area.toFixed(3)}**`, [topBase, bottomBase, height, mid, area])

  // SVG dimensions
  const vw = 380, vh = 240
  const padX = 40
  const baseY = vh - 30
  const maxBase = 24
  const maxH = 160
  const scale = Math.min((vw - padX * 2) / maxBase, maxH / maxH)
  const bottomPx = bottomBase * scale
  const topPx = topBase * scale
  const heightPx = (height / 18) * maxH
  const cx = vw / 2

  const blX = cx - bottomPx / 2
  const brX = cx + bottomPx / 2
  const tlX = cx - topPx / 2
  const trX = cx + topPx / 2
  const topY = baseY - heightPx
  const midY = baseY - heightPx / 2

  const Slider = ({ label, val, set, min, max, step }: { label: string, val: number, set: (n: number) => void, min: number, max: number, step?: number }) => (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        <span className="font-mono-math text-emerald-300">{val.toFixed(1)}</span>
      </div>
      <input type="range" min={min} max={max} step={step ?? 0.1} value={val} onChange={e => set(Number.parseFloat(e.target.value))} className="range-pro" />
    </div>
  )

  return (
    <SolverCard
      ref={ref}
      id="trapezoid"
      eyebrow="Geometry · 22,200/mo"
      title="Trapezoid Area Formula & Calculator — Drag & Solve"
      volume="Vol 22.2K"
      highlighted={highlighted}
    >
      <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-6">
        A <strong className="text-slate-200">trapezoid</strong> has two parallel bases <span className="font-mono-math text-emerald-300">a</span> (top)
        and <span className="font-mono-math text-emerald-300">b</span> (bottom) and a perpendicular height <span className="font-mono-math text-emerald-300">h</span>.
        Its area is the average of the two bases multiplied by the height:{' '}
        <span className="font-mono-math text-emerald-300">A = ((a + b) / 2) · h</span>. The dashed midsegment in the live model marks that average base.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Slider label="Top base a" val={topBase} set={setTopBase} min={1} max={24} />
          <Slider label="Bottom base b" val={bottomBase} set={setBottomBase} min={1} max={24} />
          <Slider label="Height h" val={height} set={setHeight} min={1} max={18} />

          <div className="space-y-2 pt-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Step-by-Step Solver</div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">1.</span> A = ((a + b) / 2) · h
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">2.</span> A = (({topBase.toFixed(1)} + {bottomBase.toFixed(1)}) / 2) · {height.toFixed(1)}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">3.</span> A = {mid.toFixed(2)} · {height.toFixed(1)}
            </div>
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3.5 py-2.5 font-mono-math text-sm text-emerald-200">
              <span className="text-emerald-400/80">=</span> A = {area.toFixed(3)} sq units
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2">
              <CopyButton markdown={md} />
              <EmailSolutionButton title="Trapezoid Area Formula Calculator" markdown={md} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-[#0a0e1a] p-4 blueprint-grid">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            <Triangle className="w-3 h-3" /> Blueprint Model
          </div>
          <svg viewBox={`0 0 ${vw} ${vh}`} className="w-full h-auto">
            <defs>
              <linearGradient id="trap-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6ee7ff" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#38e1a4" stopOpacity="0.08" />
              </linearGradient>
            </defs>

            {/* Trapezoid */}
            <polygon
              points={`${tlX},${topY} ${trX},${topY} ${brX},${baseY} ${blX},${baseY}`}
              fill="url(#trap-fill)"
              stroke="#38e1a4"
              strokeWidth={2}
              style={{ transition: 'all 200ms ease' }}
            />

            {/* Midsegment */}
            <line x1={(tlX + blX) / 2} y1={midY} x2={(trX + brX) / 2} y2={midY} stroke="#fbbf24" strokeWidth={1.5} strokeDasharray="5 4" />
            <text x={cx} y={midY - 6} fill="#fbbf24" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">
              mid = {mid.toFixed(2)}
            </text>

            {/* Height indicator */}
            <line x1={brX + 12} y1={topY} x2={brX + 12} y2={baseY} stroke="#6ee7ff" strokeWidth={1} />
            <line x1={brX + 8} y1={topY} x2={brX + 16} y2={topY} stroke="#6ee7ff" strokeWidth={1} />
            <line x1={brX + 8} y1={baseY} x2={brX + 16} y2={baseY} stroke="#6ee7ff" strokeWidth={1} />
            <text x={brX + 18} y={midY + 4} fill="#6ee7ff" fontSize="10" fontFamily="JetBrains Mono">h = {height.toFixed(1)}</text>

            {/* Base labels */}
            <text x={cx} y={topY - 8} fill="#94a3b8" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">a = {topBase.toFixed(1)}</text>
            <text x={cx} y={baseY + 18} fill="#94a3b8" fontSize="10" fontFamily="JetBrains Mono" textAnchor="middle">b = {bottomBase.toFixed(1)}</text>
          </svg>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Midsegment</div><div className="font-mono-math text-amber-300">{mid.toFixed(2)}</div></div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Height</div><div className="font-mono-math text-cyan-300">{height.toFixed(1)}</div></div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2"><div className="text-slate-500">Area</div><div className="font-mono-math text-emerald-300">{area.toFixed(2)}</div></div>
          </div>
        </div>
      </div>
    </SolverCard>
  )
})

TrapezoidCalculator.displayName = 'TrapezoidCalculator'
export default TrapezoidCalculator
